import React, {useState} from "react";
import {ClockIcon} from "@heroicons/react/24/outline";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import FieldCheckbox from "../fields/field-checkbox";
import ModalSaveResource from "../modal/modal-save-resource";
import {classNames} from "../../util/util-helpers";
import {toFrontDateFromUTC, toFrontDateTimeFromUTC} from "../../util/util-dates";
import CellButton from "../resource-table/table-components/cell-button";
import Tooltip from "../tooltip";

export function getReminderInfo(dateStr, isAllDay = true) {
    if (!dateStr) return {label: '', type: undefined};

    if (isAllDay) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(dateStr.replace(' ', 'T') + 'Z');
        target.setHours(0, 0, 0, 0);
        const days = Math.round((target - today) / (1000 * 60 * 60 * 24));
        if (days > 0) return {label: `In ${days}d`, type: undefined};
        if (days < 0) return {label: `${Math.abs(days)}d Due`, type: 'danger'};
        return {label: 'Today', type: 'warning'};
    }

    const now = new Date();
    const target = new Date(dateStr.replace(' ', 'T') + 'Z');
    const diffMins = Math.round((target - now) / 60000);

    function formatDuration(absMins) {
        const days = Math.floor(absMins / (60 * 24));
        const hours = Math.floor((absMins % (60 * 24)) / 60);
        const parts = [];
        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        return parts.length > 0 ? parts.join(' ') : '< 1h';
    }

    if (diffMins > 0) return {label: `Reminder in ${formatDuration(diffMins)}`, type: undefined};
    if (diffMins < 0) return {label: `Due ${formatDuration(Math.abs(diffMins))}`, type: 'danger'};
    return {label: 'Now', type: 'warning'};
}

export function ReminderCell({reminders, onSetReminderClick}) {
    const incomplete = reminders?.filter(r => !r.IsDone && r.ReminderDate);

    if (!incomplete?.length) {
        return <CellButton onClick={onSetReminderClick}>Set Reminder</CellButton>;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sorted = [...incomplete].sort((a, b) => {
        if (!a.ReminderDate) return 1;
        if (!b.ReminderDate) return -1;
        const dA = Math.round((new Date(a.ReminderDate.replace(' ', 'T') + 'Z').setHours(0, 0, 0, 0) - today) / 86400000);
        const dB = Math.round((new Date(b.ReminderDate.replace(' ', 'T') + 'Z').setHours(0, 0, 0, 0) - today) / 86400000);
        return dA - dB;
    });

    const primary = sorted[0];
    const {label, type} = getReminderInfo(primary.ReminderDate, primary.IsAllDay);
    const count = incomplete.length;

    return (
        <Tooltip
            disabled={!primary.ReminderNotes}
            content={<div className="whitespace-pre-wrap">{primary.ReminderNotes}</div>}
        >
            <div className="flex items-center gap-1">
                {count > 1 && (
                    <span
                        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                        {count}
                    </span>
                )}

                <CellButton
                    appearance={type}
                    onClick={onSetReminderClick}
                >
                    {primary.IsAllDay ? `${toFrontDateFromUTC(primary.ReminderDate)} (${label})` : `${toFrontDateTimeFromUTC(primary.ReminderDate)} (${label})`}
                </CellButton>
            </div>
        </Tooltip>
    );
}

export default function ReminderList({
                                         reminders,
                                         translate,
                                         isLoading,
                                         onIsDoneChange,
                                         onDelete,
                                         getEditFields,
                                         onEditInputChange,
                                         onEditSubmit,
                                         reminderIdKey,
                                     }) {
    const [editingReminder, setEditingReminder] = useState(null);

    function handleCardClick(reminder) {
        setEditingReminder(reminder);
    }

    function handleEditSubmit(fields) {
        onEditSubmit(editingReminder, fields);
        setEditingReminder(null);
    }

    function handleEditCancel() {
        setEditingReminder(null);
    }

    function getEditFieldsWithUniqueIds(data) {
        const fields = getEditFields(data);
        if (fields?.IsAllDay) {
            fields.IsAllDay.props = Object.assign({}, fields.IsAllDay.props, {id: 'edit_IsAllDay'});
        }
        return fields;
    }

    if (!reminders?.length) {
        return (
            <div className="px-5 py-10 text-center border-b border-tm-gray-300">
                <div className="w-10 h-10 rounded-full bg-tm-gray-100 flex items-center justify-center mx-auto mb-3">
                    <ClockIcon className="p-1 text-tm-gray-700"/>
                </div>

                <p className="text-sm font-medium text-tm-gray-900 mb-1">{translate('text.no_reminders_yet')}</p>

                <p className="text-xs text-tm-gray-600 mb-5">{translate('text.schedule_follow_up_candidate')}</p>
            </div>
        );
    }

    return (
        <>
            <div className="p-4 border-b border-tm-gray-300 space-y-2 max-h-[40vh] overflow-y-auto">
                {reminders.map((reminder) => (
                    <div key={reminder[reminderIdKey]}
                         className={classNames("rounded-lg p-3 whitespace-pre-wrap border bg-tm-gray-50 border-tm-gray-100", !isLoading && "cursor-pointer hover:bg-tm-gray-100")}
                         onClick={() => !isLoading && handleCardClick(reminder)}>

                        <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                                    <FieldCheckbox
                                        className="checkbox"
                                        onChange={(_, value) => onIsDoneChange(reminder, value)}
                                        value={reminder.IsDone}
                                    />
                                </div>

                                <span className={classNames(
                                    "text-sm font-medium px-2 py-0.5 rounded-full text-tm-gray-900",
                                    reminder.IsDone && "line-through"
                                )}>
                                    {reminder.IsAllDay ? toFrontDateFromUTC(reminder.ReminderDate) : toFrontDateTimeFromUTC(reminder.ReminderDate)}
                                </span>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    className="size-5 flex items-center justify-center rounded-btn text-tm-gray-300 hover:text-tm-gray-500 transition-colors text-base leading-none px-0.5 cursor-pointer hover:bg-tm-gray-300"
                                    title="Delete"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(reminder);
                                    }}>

                                    <XMarkIcon className="size-4 text-tm-gray-700"/>
                                </button>
                            </div>
                        </div>

                        {!!reminder.Notes && (
                            <p className={classNames(
                                "text-xs leading-snug ml-5 text-tm-gray-700 mt-2",
                                reminder.IsDone && "line-through"
                            )}>
                                {reminder.Notes}
                            </p>
                        )}
                    </div>
                ))}
            </div>

            <ModalSaveResource
                widthClass="max-w-sm"
                title={translate('modal_heading.edit_reminder')}
                show={!!editingReminder}
                isLoading={isLoading}
                onClose={handleEditCancel}
                handleInputChange={onEditInputChange}
                gridColsClass="grid-cols-2"
                fields={getEditFieldsWithUniqueIds(editingReminder)}
                buttonLabel={translate('btn.save')}
                closeButtonLabel={translate('btn.cancel')}
                translate={translate}
                onSubmit={handleEditSubmit}
                getRawFields={true}
            />
        </>
    );
}
